if (!customElements.get('estimated-arrival')) {
  customElements.define('estimated-arrival', class EstimatedArrival extends HTMLElement {
    connectedCallback() {
      this.onChange = this.onChange.bind(this);
      this.onInfoClick = this.onInfoClick.bind(this);
      this.onDocumentPointerDown = this.onDocumentPointerDown.bind(this);
      this.onKeyDown = this.onKeyDown.bind(this);
      this.onFocusOut = this.onFocusOut.bind(this);
      this.onMouseEnter = this.onMouseEnter.bind(this);
      this.onMouseLeave = this.onMouseLeave.bind(this);
      this.onFocusIn = this.onFocusIn.bind(this);
      this.onResize = this.positionInfo.bind(this);
      this.tooltip = this.querySelector('.wt-estimated-arrival__tooltip');
      this.infoButton = this.querySelector('.wt-estimated-arrival__info-button');
      this.infoPanel = this.querySelector('.wt-estimated-arrival__info');
      this.addEventListener('change', this.onChange);
      this.addEventListener('keydown', this.onKeyDown);
      this.tooltip?.addEventListener('mouseenter', this.onMouseEnter);
      this.tooltip?.addEventListener('mouseleave', this.onMouseLeave);
      this.tooltip?.addEventListener('focusin', this.onFocusIn);
      this.tooltip?.addEventListener('focusout', this.onFocusOut);
      this.infoButton?.addEventListener('click', this.onInfoClick);
      window.addEventListener('resize', this.onResize);
      this.render();
    }

    disconnectedCallback() {
      clearTimeout(this.closeInfoTimer);
      this.removeEventListener('change', this.onChange);
      this.removeEventListener('keydown', this.onKeyDown);
      this.tooltip?.removeEventListener('mouseenter', this.onMouseEnter);
      this.tooltip?.removeEventListener('mouseleave', this.onMouseLeave);
      this.tooltip?.removeEventListener('focusin', this.onFocusIn);
      this.tooltip?.removeEventListener('focusout', this.onFocusOut);
      this.infoButton?.removeEventListener('click', this.onInfoClick);
      window.removeEventListener('resize', this.onResize);
      document.removeEventListener('pointerdown', this.onDocumentPointerDown);
    }

    onChange(event) {
      if (event.target.matches('input[type="radio"]')) this.render();
    }

    onInfoClick() {
      this.openInfo();
    }

    onMouseEnter() {
      this.openInfo();
    }

    onMouseLeave() {
      clearTimeout(this.closeInfoTimer);
      this.closeInfoTimer = setTimeout(() => {
        if (!this.tooltip.matches(':hover') && !this.infoButton.matches(':focus-visible')) this.closeInfo();
      }, 250);
    }

    onFocusIn() {
      this.openInfo();
    }

    openInfo() {
      clearTimeout(this.closeInfoTimer);
      if (!this.infoPanel || !this.infoPanel.hidden) return;
      this.infoPanel.hidden = false;
      this.infoButton.setAttribute('aria-expanded', 'true');
      this.positionInfo();
      document.addEventListener('pointerdown', this.onDocumentPointerDown);
    }

    onDocumentPointerDown(event) {
      if (!this.infoButton.contains(event.target) && !this.infoPanel.contains(event.target)) this.closeInfo();
    }

    onKeyDown(event) {
      if (event.key === 'Escape' && !this.infoPanel?.hidden) {
        event.preventDefault();
        this.closeInfo();
        this.infoButton.focus();
      }
    }

    onFocusOut(event) {
      if (!this.tooltip.contains(event.relatedTarget) && !this.tooltip.matches(':hover')) this.closeInfo();
    }

    closeInfo() {
      clearTimeout(this.closeInfoTimer);
      if (!this.infoPanel || this.infoPanel.hidden) return;
      this.infoPanel.hidden = true;
      this.infoPanel.classList.remove('wt-estimated-arrival__info--above');
      this.infoButton.setAttribute('aria-expanded', 'false');
      document.removeEventListener('pointerdown', this.onDocumentPointerDown);
    }

    positionInfo() {
      if (!this.infoPanel || this.infoPanel.hidden || !this.tooltip || !this.infoButton) return;
      const boundary = this.closest('.wt-product__main') || this.parentElement;
      if (!boundary) return;
      const boundaryRect = boundary.getBoundingClientRect();
      const tooltipRect = this.tooltip.getBoundingClientRect();
      const buttonRect = this.infoButton.getBoundingClientRect();
      const padding = 8;
      const maxWidth = Math.max(0, boundaryRect.width - padding * 2);
      const width = Math.min(this.infoPanel.getBoundingClientRect().width, maxWidth);
      const left = Math.min(
        Math.max(buttonRect.left + buttonRect.width / 2 - width / 2, boundaryRect.left + padding),
        boundaryRect.right - padding - width
      );
      this.tooltip.style.setProperty('--wt-estimated-arrival-tooltip-width', `${width}px`);
      this.tooltip.style.setProperty('--wt-estimated-arrival-tooltip-left', `${left - tooltipRect.left}px`);
      this.infoPanel.style.setProperty('--wt-estimated-arrival-tooltip-width', `${width}px`);
      this.infoPanel.style.setProperty('--wt-estimated-arrival-tooltip-left', `${left - tooltipRect.left}px`);
      this.infoPanel.style.setProperty('--wt-estimated-arrival-tooltip-arrow-left', `${buttonRect.left + buttonRect.width / 2 - left}px`);

      if (this.dataset.dayUnit === 'business') {
        this.infoPanel.style.removeProperty('max-height');
        this.infoPanel.classList.remove('wt-estimated-arrival__info--above');
        const header = document.querySelector('header.page-header');
        const headerBottom = header && ['sticky', 'fixed'].includes(getComputedStyle(header).position)
          ? Math.max(0, header.getBoundingClientRect().bottom) : 0;
        const spaceAbove = Math.max(0, buttonRect.top - Math.max(padding, headerBottom + padding) - 10);
        const spaceBelow = Math.max(0, window.innerHeight - buttonRect.bottom - 10 - padding);
        const height = this.infoPanel.getBoundingClientRect().height;
        const above = height > spaceBelow && spaceAbove > spaceBelow;
        this.infoPanel.classList.toggle('wt-estimated-arrival__info--above', above);
        this.infoPanel.style.maxHeight = `${above ? spaceAbove : spaceBelow}px`;
        return;
      }
      const infoRect = this.infoPanel.getBoundingClientRect();
      const canFlipAbove = buttonRect.top - infoRect.height - 10 >= boundaryRect.top + padding;
      this.infoPanel.classList.toggle('wt-estimated-arrival__info--above', infoRect.bottom > boundaryRect.bottom - padding && canFlipAbove);
    }

    parseRange(value) {
      const match = /^\s*(\d+)\s*[-–—]\s*(\d+)(?:\s+(?:calendar\s+)?days?)?\s*$/i.exec(value || '');
      if (!match) return null;
      const from = Number(match[1]);
      const to = Number(match[2]);
      return from <= to && to <= 365 ? [from, to] : null;
    }

    formatRange(range) {
      return range[0] === range[1] ? String(range[0]) : range.join('–');
    }

    addBusinessDays(start, days) {
      const date = new Date(start);
      for (let added = 0; added < days;) {
        date.setDate(date.getDate() + 1);
        if (date.getDay() !== 0 && date.getDay() !== 6) added++;
      }
      return date;
    }

    parseShippingRanges(value) {
      const ranges = {};
      for (const part of (value || '').split(';')) {
        const match = /^\s*(standard|expedited)\s*:\s*(.+)\s*$/i.exec(part);
        if (!match) return null;
        const method = match[1].toLowerCase();
        const range = this.parseRange(match[2]);
        if (!range || ranges[method]) return null;
        ranges[method] = range;
      }
      return ranges.standard && ranges.expedited ? ranges : null;
    }

    addCalendarDays(start, days) {
      const date = new Date(start);
      date.setDate(date.getDate() + days);
      return date;
    }

    businessToday(now = new Date()) {
      const parts = new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Hong_Kong', year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(now);
      const value = (type) => Number(parts.find((part) => part.type === type).value);
      return new Date(Date.UTC(value('year'), value('month') - 1, value('day')));
    }

    isTransitHoliday(date) {
      const year = date.getUTCFullYear();
      const key = (day) => day.toISOString().slice(0, 10);
      const observed = (month, day, targetYear = year) => {
        const result = new Date(Date.UTC(targetYear, month, day));
        if (result.getUTCDay() === 6) result.setUTCDate(result.getUTCDate() - 1);
        if (result.getUTCDay() === 0) result.setUTCDate(result.getUTCDate() + 1);
        return key(result);
      };
      const nthMonday = (month, nth) => {
        const first = new Date(Date.UTC(year, month, 1));
        return key(new Date(Date.UTC(year, month, 1 + (8 - first.getUTCDay()) % 7 + (nth - 1) * 7)));
      };
      const mayEnd = new Date(Date.UTC(year, 4, 31));
      mayEnd.setUTCDate(31 - (mayEnd.getUTCDay() + 6) % 7);
      const november = new Date(Date.UTC(year, 10, 1));
      const thanksgiving = key(new Date(Date.UTC(year, 10, 1 + (11 - november.getUTCDay()) % 7 + 21)));
      return new Set([observed(0, 1), observed(0, 1, year + 1), nthMonday(0, 3), nthMonday(1, 3), key(mayEnd), observed(5, 19), observed(6, 4), nthMonday(8, 1), thanksgiving, observed(11, 25)]).has(key(date));
    }

    addPreviewDays(start, days, transit = false) {
      const result = new Date(start);
      for (let added = 0; added < days;) {
        result.setUTCDate(result.getUTCDate() + 1);
        if (result.getUTCDay() !== 0 && result.getUTCDay() !== 6 && (!transit || !this.isTransitHoliday(result))) added++;
      }
      return result;
    }

    businessArrival(start, production, shipping) {
      const dayZero = new Date(start);
      while (dayZero.getUTCDay() === 0 || dayZero.getUTCDay() === 6) dayZero.setUTCDate(dayZero.getUTCDate() + 1);
      return this.addPreviewDays(this.addPreviewDays(dayZero, production), shipping, true);
    }

    render() {
      const method = this.querySelector('input[type="radio"]:checked')?.value || 'standard';
      const resolvedTimings = this.dataset.useResolvedTimings === 'true' || this.dataset.calendarDays === 'true';
      const useBusinessDays = this.dataset.dayUnit === 'business';
      const process = this.parseRange(resolvedTimings ? this.dataset.productionOverride : this.dataset.processTime);
      const shippingRanges = resolvedTimings ? this.parseShippingRanges(this.dataset.shippingOverride) : null;
      const shippingValue = method === 'expedited' ? this.dataset.shippingTimeExpedited : this.dataset.shippingTimeStandard;
      const shipping = resolvedTimings ? shippingRanges?.[method] : this.parseRange(shippingValue);
      const dateEl = this.querySelector('.wt-estimated-arrival__date');
      this.querySelectorAll('[data-method-tooltip]').forEach((tooltip) => {
        tooltip.hidden = tooltip.dataset.methodTooltip !== method;
      });
      if (!dateEl) return;
      if (!process || !shipping) {
        dateEl.textContent = '';
        if (resolvedTimings) this.hidden = true;
        return;
      }

      if (resolvedTimings) {
        this.hidden = false;
        this.querySelectorAll('[data-shipping-duration]').forEach((el) => {
          const range = shippingRanges[el.dataset.shippingDuration];
          el.textContent = `${this.formatRange(range)} ${useBusinessDays ? this.dataset.businessUnit : this.dataset.calendarUnit}`;
        });
        this.querySelectorAll('[data-method-tooltip]').forEach((el) => {
          const range = shippingRanges[el.dataset.methodTooltip];
          const total = [process[0] + range[0], process[1] + range[1]];
          const text = ((useBusinessDays ? this.dataset.businessTooltip : this.dataset.calendarTooltip) || '')
            .replace('__production__', this.formatRange(process))
            .replace('__shipping__', this.formatRange(range))
            .replace('__total__', this.formatRange(total));
          if (useBusinessDays) {
            el.replaceChildren(...text.split('\n').map((line, index) => {
              const row = document.createElement('span');
              row.className = index < 3 ? 'wt-estimated-arrival__info-metric' : 'wt-estimated-arrival__info-note';
              if (index < 3 && line.includes(':')) {
                const label = document.createElement('span');
                const value = document.createElement('strong');
                const separator = line.indexOf(':');
                label.textContent = line.slice(0, separator);
                value.textContent = line.slice(separator + 1).trim();
                row.append(label, value);
              } else {
                row.textContent = line;
              }
              return row;
            }));
          } else {
            el.textContent = text;
          }
        });
      }

      const today = useBusinessDays ? this.businessToday() : new Date();
      const addDays = resolvedTimings ? this.addCalendarDays : this.addBusinessDays;
      const earliest = useBusinessDays ? this.businessArrival(today, process[0], shipping[0]) : addDays.call(this, today, process[0] + shipping[0]);
      const latest = useBusinessDays ? this.businessArrival(today, process[1], shipping[1]) : addDays.call(this, today, process[1] + shipping[1]);
      dateEl.textContent = `${this.formatDate(earliest)} - ${this.formatDate(latest)}`;
    }

    formatDate(date) {
      const locale = document.documentElement.lang || 'en';
      const isEnglish = /^en\b/i.test(locale);
      const value = new Intl.DateTimeFormat(isEnglish ? 'en-US' : locale, { month: 'short', day: 'numeric', ...(this.dataset.dayUnit === 'business' ? { timeZone: 'UTC' } : {}) }).format(date);
      return isEnglish ? value.replace(/^([A-Za-z]{3})(?=\s)/, (month) => month === 'May' ? month : `${month}.`) : value;
    }
  });
}
